import { Link, useLocation } from "react-router-dom";
import {
  Search, HelpCircle, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/App";
import NotificationBell from "@/components/NotificationBell";
import WalletChip from "@/components/WalletChip";
import Breadcrumbs from "@/components/Breadcrumbs";

/**
 * Identidade visual compartilhada por todas as páginas autenticadas do Hermes.
 * - <TopBar>       — Barra de busca + notificações + menu do usuário
 * - <PageHero>     — Cabeçalho editorial (eyebrow em maiúsculas + título serifado + subtítulo)
 * - <PageShell>    — Wrapper que compõe TopBar + PageHero + espaço para conteúdo
 *
 * Uso mínimo:
 *   <PageShell eyebrow="HERMES · DJEN" title="Publicações oficiais" subtitle="…">
 *     ...conteúdo da página...
 *   </PageShell>
 */

const NAVY = "#0E1D2D";
const GOLD = "#C9953D";
const BORDER = "#E7E5E2";
const TEXT = "#132238";

export function TopBar() {
  const { user } = useAuth();
  return (
    <div className="sticky top-0 z-30 bg-white/70 backdrop-blur border-b" style={{ borderColor: BORDER }}>
      <header className="h-16 flex items-center gap-4 px-3 sm:px-6" data-testid="hermes-topbar">
        <div className="flex-1 max-w-2xl">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="global-search"
              placeholder="Buscar processos, clientes, documentos, prazos ou perguntar ao HERMES..."
              className="w-full h-10 pl-9 pr-16 rounded-full bg-[#F4F2EF] border border-transparent focus:border-[#C9953D]/40 focus:bg-white text-sm text-slate-700 placeholder:text-slate-400 outline-none transition"
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 bg-white">⌘K</kbd>
          </div>
        </div>
        <NotificationBell/>
        <WalletChip/>
        <button className="w-10 h-10 rounded-full hover:bg-slate-100 inline-flex items-center justify-center" title="Ajuda">
          <HelpCircle className="w-5 h-5 text-slate-500"/>
        </button>
        <div className="h-8 w-px bg-slate-200"/>
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full ring-2 ring-white shadow-sm"/>
          ) : (
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0E1D2D] to-[#12273A] text-[#D2A85A] font-serif text-sm inline-flex items-center justify-center">
              {(user?.name || "H")[0]}
            </div>
          )}
          <div className="hidden md:block leading-tight text-right">
            <div className="text-sm font-semibold text-[#132238]">{user?.name || "—"}</div>
            <div className="text-[11px] text-slate-500 truncate max-w-[160px]">{user?.email}</div>
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400"/>
        </div>
      </header>
      <BreadcrumbsBar/>
    </div>
  );
}

function BreadcrumbsBar() {
  const { pathname } = useLocation();
  const depth = pathname.split("/").filter(Boolean).length;
  if (depth < 2) return null;
  return (
    <div className="px-3 sm:px-6 py-1.5 border-t" style={{ borderColor: BORDER }}>
      <Breadcrumbs/>
    </div>
  );
}

export function PageHero({ eyebrow, title, titleItalic, subtitle, icon: Icon, right }) {
  return (
    <section className="mb-6 flex items-start justify-between gap-6" data-testid="page-hero">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.25em] font-sans font-semibold flex items-center gap-2" style={{ color: GOLD }}>
          {Icon && <Icon className="w-3.5 h-3.5"/>}
          {eyebrow}
        </div>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight" style={{ color: TEXT }}>
          {title} {titleItalic && <span className="italic">{titleItalic}</span>}
        </h1>
        {subtitle && (
          <p className="mt-3 text-[15px] text-slate-600 max-w-3xl">{subtitle}</p>
        )}
      </div>
      {right && <div className="shrink-0">{right}</div>}
    </section>
  );
}

export default function PageShell({
  eyebrow,
  title,
  titleItalic,
  subtitle,
  icon,
  right,
  children,
  className = "",
  testid,
}) {
  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid={testid || "page-shell"}>
      <TopBar/>
      <main className={`flex-1 p-3 sm:p-6 lg:p-8 font-sans ${className}`} style={{ color: TEXT }}>
        <PageHero eyebrow={eyebrow} title={title} titleItalic={titleItalic} subtitle={subtitle} icon={icon} right={right}/>
        {children}
      </main>
    </div>
  );
}
// Cartão padrão do Hermes: fundo branco, borda creme, cantos 2xl
export function HermesCard({ className = "", children, ...rest }) {
  return (
    <div
      className={`bg-white rounded-2xl border p-5 ${className}`}
      style={{ borderColor: BORDER }}
      {...rest}
    >
      {children}
    </div>
  );
}
